import { useEffect, useState } from "react";
import { createAmenity, updateAmenity } from "../../../services/amenityService";
import Select, { components } from 'react-select';
import FormField from "../../../components/ui/FormField";
import TextInput from "../../../components/ui/TextInput";
import NumberInput from "../../../components/ui/NumberInput";
import Textarea from "../../../components/ui/Textarea";
import SelectDropdown from "../../../components/ui/SelectDropdown";
import { TimePicker } from "../../../components/ui/DateTimePickers";
import ToggleSwitch from "../../../components/ui/ToggleSwitch";
import Button from "../../../components/ui/Button";
import { Plus, Trash2 } from "lucide-react";
import { toast } from 'react-toastify';
import { AMENITY_ICONS } from "../../../utils/IconsList";
import styles from "../../../components/users/UserForm.module.css";
import "./AddAmenities.css";

const ICON_OPTIONS = AMENITY_ICONS;

const IconOption = (props) => {
  const IconCmp = props.data.icon;
  return (
    <components.Option {...props}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span style={{ marginRight: '8px', display: 'flex' }}>
          {IconCmp && <IconCmp />}
        </span>
        {props.data.label}
      </div>
    </components.Option>
  );
};

const IconSingleValue = (props) => {
  const IconCmp = props.data.icon;
  return (
    <components.SingleValue {...props}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span style={{ marginRight: '8px', display: 'flex' }}>
          {IconCmp && <IconCmp />}
        </span>
        {props.data.label}
      </div>
    </components.SingleValue>
  );
};

const AddAmenity = ({ amenity, societies = [], readOnly = false, onSuccess }) => {
  const [form, setForm] = useState({
    name: "",
    society_id: "",
    type: "activity",
    description: "",
    rules: "",
    booking_fee: "",
    max_booking_hours: "2",
    advance_booking_days: "30",
    opening_time: "06:00",
    closing_time: "22:00",
    booking_options: [],
    min_booking_size: 1,
    max_booking_size: "",
    outsider_fee: 0,
  });
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [enableBookingSize, setEnableBookingSize] = useState(false);

  useEffect(() => {
    if (amenity) {
      setForm({
        name: amenity.name || "",
        society_id: amenity.society_id || "",
        type: amenity.type || "activity",
        description: amenity.description || "",
        rules: amenity.rules || "",
        booking_fee: amenity.booking_fee || "",
        max_booking_hours: amenity.max_booking_hours || "2",
        advance_booking_days: amenity.advance_booking_days || "30",
        opening_time: (() => {
          if (!amenity.opening_time) return "06:00";
          const str = String(amenity.opening_time);
          const match = str.match(/(\d{2}):(\d{2})/);
          if (match) return `${match[1]}:${match[2]}`;
          try {
            const d = new Date(amenity.opening_time);
            return !isNaN(d.getTime()) ? d.toISOString().substring(11, 16) : "06:00";
          } catch (_) {
            return "06:00";
          }
        })(),
        closing_time: (() => {
          if (!amenity.closing_time) return "22:00";
          const str = String(amenity.closing_time);
          const match = str.match(/(\d{2}):(\d{2})/);
          if (match) return `${match[1]}:${match[2]}`;
          try {
            const d = new Date(amenity.closing_time);
            return !isNaN(d.getTime()) ? d.toISOString().substring(11, 16) : "22:00";
          } catch (_) {
            return "22:00";
          }
        })(),
        booking_options: Array.isArray(amenity.booking_options) 
          ? amenity.booking_options.map(opt => typeof opt === 'string' ? { label: opt, icon: '' } : opt)
          : [],
        min_booking_size: amenity.min_booking_size || 1,
        max_booking_size: amenity.max_booking_size || "",
        outsider_fee: amenity.outsider_fee || 0,
      });

      if (amenity.min_booking_size || amenity.max_booking_size) {
        setEnableBookingSize(true);
      }
    }
  }, [amenity]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOptionChange = (index, field, value) => {
    setForm((prev) => {
      const nextOptions = [...prev.booking_options];
      nextOptions[index] = {
        ...nextOptions[index],
        [field]: value,
      };
      return { ...prev, booking_options: nextOptions };
    });
  };

  const addOption = () => {
    setForm((prev) => ({
      ...prev,
      booking_options: [...prev.booking_options, { label: "", icon: "" }],
    }));
  };

  const removeOption = (index) => {
    setForm((prev) => ({
      ...prev,
      booking_options: prev.booking_options.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;
    setError("");

    if (!form.name.trim()) {
      setError("Amenity Name is required");
      return;
    }
    if (!form.society_id) {
      setError("Please select a Society");
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = {
        ...form,
        society_id: Number(form.society_id),
        booking_fee: form.booking_fee ? Number(form.booking_fee) : 0,
        max_booking_hours: Number(form.max_booking_hours) || 2,
        advance_booking_days: Number(form.advance_booking_days) || 30,
        min_booking_size: enableBookingSize ? (Number(form.min_booking_size) || 1) : null,
        max_booking_size: enableBookingSize && form.max_booking_size ? Number(form.max_booking_size) : null,
        outsider_fee: form.type === "activity" ? (Number(form.outsider_fee) || 0) : 0,
      };

      if (amenity?.amenity_id) {
        await updateAmenity(amenity.amenity_id, payload);
        toast.success("Amenity updated successfully!");
      } else {
        await createAmenity(payload);
        toast.success("Amenity created successfully!");
      }

      if (onSuccess) onSuccess();
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.message || err.message || "Failed to save amenity";
      setError(msg);
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={styles.formContainer}>
      <div className={styles.header}>
        <div>
          <h3 className={styles.headerTitle}>{amenity ? "Edit Amenity Details" : "Amenity Details"}</h3>
          <p className={styles.headerHint}>
            Fill in the amenity details and operational rules.
          </p>
        </div>
      </div>

      {error && <p className={styles.error}>{error}</p>}
      
      <div className={styles.grid}>
        <FormField label="Amenity Name" required>
          <TextInput 
            name="name" 
            value={form.name} 
            onChange={handleChange} 
            placeholder="Swimming Pool, Gym, etc." 
            disabled={readOnly}
            required
          />
        </FormField>

        <FormField label="Society / Property" required>
          <SelectDropdown 
            value={form.society_id} 
            onChange={(e) => handleChange({ target: { name: 'society_id', value: e.target.value } })} 
            disabled={readOnly}
            options={[
              { label: "Select Society", value: "" },
              ...societies.map((soc) => ({
                label: soc.society_name,
                value: String(soc.society_id),
              }))
            ]}
          />
        </FormField>

        <FormField label="Amenity Type" required>
          <SelectDropdown 
            value={form.type} 
            onChange={(e) => handleChange({ target: { name: 'type', value: e.target.value } })} 
            disabled={readOnly}
            options={[
              { label: "Activity (Pool, Gym, etc)", value: "activity" },
              { label: "Event (Banquet Hall, Ground, etc)", value: "event" },
              { label: "General Booking", value: "booking" }
            ]}
          />
        </FormField>

        <div className={styles.fullSpan}>
          <FormField label="Booking Options / Categories">
            {form.booking_options.map((opt, index) => (
              <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                <TextInput 
                  name={`booking_options_${index}_label`}
                  value={opt.label} 
                  onChange={(e) => handleOptionChange(index, 'label', e.target.value)} 
                  style={{ flex: 1 }}
                  placeholder="Label (e.g. Wedding, Birthday)" 
                  disabled={readOnly}
                />
                <div style={{ flex: 1, minWidth: '150px' }}>
                  <Select
                    value={ICON_OPTIONS.find(o => o.value === opt.icon) || null}
                    onChange={(selected) => handleOptionChange(index, 'icon', selected ? selected.value : '')}
                    options={ICON_OPTIONS}
                    components={{ Option: IconOption, SingleValue: IconSingleValue }}
                    isDisabled={readOnly}
                    placeholder="Select Icon..."
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                  />
                </div>
                {!readOnly && (
                  <Button 
                    type="button" 
                    variant="danger"
                    size="sm"
                    onClick={() => removeOption(index)} 
                  >
                    <Trash2 size={14} />
                  </Button>
                )}
              </div>
            ))}
            {!readOnly && (
              <Button 
                type="button" 
                variant="outline"
                size="sm"
                onClick={addOption} 
              >
                <Plus size={14} style={{ marginRight: "4px" }} /> Add Option
              </Button>
            )}
          </FormField>
        </div>

        <FormField label="Booking Fee (₹)" helperText="Optional">
          <NumberInput 
            name="booking_fee" 
            value={form.booking_fee} 
            onChange={handleChange} 
            placeholder="0.00" 
            disabled={readOnly}
          />
        </FormField>

        <div className={styles.fullSpan}>
          <ToggleSwitch 
            checked={enableBookingSize}
            onChange={(e) => setEnableBookingSize(e.target.checked)}
            disabled={readOnly}
            label="Limit Booking Group Size"
            description="Enforces minimum and maximum person capacity per slot"
          />
        </div>

        {enableBookingSize && (
          <>
            <FormField label="Min Booking Size">
              <NumberInput 
                name="min_booking_size" 
                value={form.min_booking_size} 
                onChange={handleChange} 
                placeholder="1" 
                disabled={readOnly}
              />
            </FormField>

            <FormField label="Max Booking Size" helperText="Optional">
              <NumberInput 
                name="max_booking_size" 
                value={form.max_booking_size} 
                onChange={handleChange} 
                placeholder="No limit" 
                disabled={readOnly}
              />
            </FormField>
          </>
        )}

        {form.type === "activity" && (
          <FormField label="Outsider / Guest Fee (₹)">
            <NumberInput 
              name="outsider_fee" 
              value={form.outsider_fee} 
              onChange={handleChange} 
              placeholder="0.00" 
              disabled={readOnly}
            />
          </FormField>
        )}

        <FormField label="Max Booking Hours">
          <NumberInput 
            name="max_booking_hours" 
            value={form.max_booking_hours} 
            onChange={handleChange} 
            placeholder="2" 
            disabled={readOnly}
          />
        </FormField>

        <FormField label="Advance Booking Days">
          <NumberInput 
            name="advance_booking_days" 
            value={form.advance_booking_days} 
            onChange={handleChange} 
            placeholder="30" 
            disabled={readOnly}
          />
        </FormField>

        <FormField label="Opening Time">
          <TimePicker 
            name="opening_time" 
            value={form.opening_time} 
            onChange={handleChange} 
            disabled={readOnly}
          />
        </FormField>

        <FormField label="Closing Time">
          <TimePicker 
            name="closing_time" 
            value={form.closing_time} 
            onChange={handleChange} 
            disabled={readOnly}
          />
        </FormField>

        <div className={styles.fullSpan}>
          <FormField label="Description">
            <Textarea 
              name="description" 
              value={form.description} 
              onChange={handleChange} 
              placeholder="General information about the amenity" 
              rows={2} 
              disabled={readOnly}
            />
          </FormField>
        </div>

        <div className={styles.fullSpan}>
          <FormField label="Rules & Policies">
            <Textarea 
              name="rules" 
              value={form.rules} 
              onChange={handleChange} 
              placeholder="Important instructions or rules for users" 
              rows={2} 
              disabled={readOnly}
            />
          </FormField>
        </div>
      </div>

      {!readOnly && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px" }}>
          <Button type="submit" disabled={isSubmitting} loading={isSubmitting}>
            {amenity ? "Update Amenity" : "Create Amenity"}
          </Button>
        </div>
      )}
    </form>
  );
};

export default AddAmenity;
